import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Textarea,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import { RECONCILIATION_COMMENT_MAX_LENGTH } from '@billing/validation';
import { IconPlus } from '@tabler/icons-react';
import { groupDebitEOBs } from 'utils/eobUtils';
import { DateTime } from 'luxon';
import {
  type ClaimTableRow,
  getClaimTariffRowDebits,
  isClaimTariffRow,
} from 'pages/claims/utils/claimTableRows';
import type { Debit } from 'interfaces';
import { buttonTextColor } from 'utils/buttonUtils';
import { formatCurrency, toMoneyString } from 'utils/currencyUtils';
import {
  hasPaidTariffDebits,
  PaidTariffPaymentSummary,
} from './PaidTariffPaymentSummary';

const TARIFF_FALLBACK = 'Tariff';
const CLAIM_NUMBER_FALLBACK = 'No claim number';

interface ReconcilePreviewRow {
  rowID: string;
  rejected: boolean;
  claimID: string;
  claimLabel: string | null;
  tariffLabel: string | null;
  requestedAmount: string;
  paidAmount: string;
  adjustmentAmount: string;
}

interface ClaimBulkReconcileModalProps {
  open: boolean;
  rows: ClaimTableRow[];
  onClose: () => void;
  onConfirm: (
    recordAdjustment: boolean,
    comment?: string,
    internalComment?: string
  ) => Promise<void> | void;
}

const getTariffLabel = (row: ClaimTableRow): string | null => {
  const tariff = row.tariffs?.[0];
  if (!tariff) return null;
  return `${tariff.prefix ?? ''}${tariff.code ?? ''}`.trim() || null;
};

const getDebitCreatedAtMillis = (debit: Debit) => {
  if (!debit.createdAt) return 0;
  const createdAt = DateTime.fromISO(debit.createdAt, { setZone: true });
  return createdAt.isValid ? createdAt.toMillis() : 0;
};

const getLatestDebit = (debits: Debit[]) =>
  [...debits].sort(
    (left, right) =>
      getDebitCreatedAtMillis(right) - getDebitCreatedAtMillis(left)
  )[0] ?? null;

const buildPreviewRow = (row: ClaimTableRow): ReconcilePreviewRow => {
  const debits = isClaimTariffRow(row)
    ? getClaimTariffRowDebits(row)
    : row.debits ?? [];
  const latestDebit = getLatestDebit(debits);
  const requestedAmount = Number(latestDebit?.requestedAmount ?? 0);
  const paidTotal = debits.reduce(
    (sum, debit) => sum + Number(debit.paidAmount ?? 0),
    0
  );
  const adjustmentTotal = requestedAmount - paidTotal;
  const claimLabel =
    row.parentClaim?.claimNumber?.trim() || row.claimNumber?.trim() || null;

  return {
    rowID: row.rowId,
    rejected: row.tariffs?.[0]?.status === CLAIM_TARIFF_STATUS.REJECTED,
    claimID: row.parentClaimId || row.id,
    claimLabel,
    tariffLabel: getTariffLabel(row),
    requestedAmount: toMoneyString(requestedAmount),
    paidAmount: toMoneyString(paidTotal),
    adjustmentAmount: toMoneyString(adjustmentTotal),
  };
};

export const ClaimBulkReconcileModal = ({
  open,
  rows,
  onClose,
  onConfirm,
}: ClaimBulkReconcileModalProps) => {
  const theme = useMantineTheme();
  const mode = useComputedColorScheme('dark');
  const { translate, getLocale } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [internalComment, setInternalComment] = useState('');
  const [saveError, setSaveError] = useState(false);
  const commentsOverLimit =
    comment.length > RECONCILIATION_COMMENT_MAX_LENGTH ||
    internalComment.length > RECONCILIATION_COMMENT_MAX_LENGTH;
  useEffect(() => {
    setComment('');
    setInternalComment('');
    setSaveError(false);
  }, [open, rows]);
  const locale = getLocale() ?? 'en-CA';
  const tariffFallbackLabel = translate(
    'pages:claims.bulkReconcile.tariffFallback',
    TARIFF_FALLBACK
  );
  const claimNumberFallbackLabel = translate(
    'pages:claims.bulkReconcile.claimNumberFallback',
    CLAIM_NUMBER_FALLBACK
  );

  const previewRows = useMemo(
    () =>
      rows
        .flatMap((row) => (isClaimTariffRow(row) ? [row] : row.subRows ?? []))
        .map(buildPreviewRow),
    [rows]
  );
  const paymentSummary = useMemo(() => {
    const tariffRows = rows.flatMap((row) =>
      isClaimTariffRow(row) ? [row] : row.subRows ?? []
    );

    return {
      tariffs: tariffRows.flatMap((row) => row.tariffs ?? []),
      debits: tariffRows.flatMap((row) => getClaimTariffRowDebits(row)),
      timezone: tariffRows[0]?.parentClaim?.site?.timezone,
    };
  }, [rows]);
  const eobs = useMemo(
    () => groupDebitEOBs(paymentSummary.debits),
    [paymentSummary.debits]
  );
  const hasPayments = hasPaidTariffDebits(paymentSummary.debits);

  const rejectedCount = previewRows.filter((row) => row.rejected).length;
  const hasRejections = rejectedCount > 0;
  const onlyRejections = hasRejections && rejectedCount === previewRows.length;
  const isSingleTariff = previewRows.length === 1;
  const title = isSingleTariff
    ? translate(
        'pages:claims.bulkReconcile.title_one',
        'Reconcile Selected Tariff'
      )
    : translate(
        'pages:claims.bulkReconcile.title_other',
        'Reconcile Selected Tariffs'
      );
  const description = isSingleTariff
    ? translate(
        'pages:claims.bulkReconcile.description_one',
        'Only the tariff listed below will be reconciled. Requested shows the latest request for the tariff, not the sum of repeated requests.'
      )
    : translate(
        'pages:claims.bulkReconcile.description_other',
        'Only the tariffs listed below will be reconciled. Requested shows the latest request for each tariff, not the sum of repeated requests.'
      );

  const handleConfirm = async (recordAdjustment: boolean) => {
    if (loading || commentsOverLimit) return;
    setLoading(true);
    setSaveError(false);
    try {
      await onConfirm(recordAdjustment, comment.trim(), internalComment.trim());
      onClose();
    } catch {
      setSaveError(true);
    } finally {
      setLoading(false);
    }
  };

  const amountColumns: Array<{
    key: keyof Pick<
      ReconcilePreviewRow,
      'requestedAmount' | 'paidAmount' | 'adjustmentAmount'
    >;
    label: string;
    color?: string;
  }> = [
    {
      key: 'requestedAmount',
      label: translate(
        'pages:claims.bulkReconcile.columns.requested',
        'Requested'
      ),
    },
    {
      key: 'paidAmount',
      label: translate('pages:claims.bulkReconcile.columns.paid', 'Paid'),
    },
    {
      key: 'adjustmentAmount',
      label: translate(
        'pages:claims.bulkReconcile.columns.adjustment',
        'Adjustment'
      ),
      color: theme.colors[`warning-yellow-${mode}`][0],
    },
  ];

  return (
    <Modal
      opened={open}
      onClose={() => {
        if (!loading) onClose();
      }}
      closeOnEscape={!loading}
      closeOnClickOutside={!loading}
      withCloseButton={!loading}
      size="lg"
      centered
      radius="lg"
      shadow="xl"
      overlayProps={{ blur: 3, opacity: 0.5 }}
      title={<Text fw={700}>{title}</Text>}
      styles={{
        content: {
          background:
            mode === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
          border:
            mode === 'dark'
              ? `1px solid ${theme.colors.dark[4]}`
              : `1px solid ${theme.colors.gray[3]}`,
        },
        header: { paddingBottom: 0 },
        title: {
          color: mode === 'dark' ? theme.white : undefined,
        },
        close: {
          border: `1px solid ${
            mode === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]
          }`,
          background: mode === 'dark' ? theme.colors.dark[6] : theme.white,
          borderRadius: 10,
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${
              mode === 'dark' ? theme.colors.blue[6] : theme.colors.blue[5]
            }`,
          },
        },
      }}
    >
      <Stack gap="md">
        {hasPayments ? (
          <PaidTariffPaymentSummary {...paymentSummary} showWarning={false} />
        ) : null}
        <Text size="sm" c="dimmed">
          {description}
        </Text>

        {hasRejections && (
          <Text size="sm">
            {translate(
              'pages:claims.bulkReconcile.rejectionExplanation',
              'Rejected tariffs will be marked Unclaimable. Their debit amounts will stay unchanged. Underpaid and overpaid tariffs will be marked Paid.'
            )}
          </Text>
        )}

        <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
          <ScrollArea.Autosize mah={360} type="auto">
            <Table
              striped
              highlightOnHover
              horizontalSpacing="xs"
              verticalSpacing={6}
              fz="xs"
              style={{ tableLayout: 'fixed' }}
            >
              <Table.Thead
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  background:
                    mode === 'dark'
                      ? theme.colors.dark[6]
                      : theme.colors.gray[1],
                }}
              >
                <Table.Tr>
                  <Table.Th w="24%">
                    {translate(
                      'pages:claims.bulkReconcile.columns.claim',
                      'Claim'
                    )}
                  </Table.Th>
                  <Table.Th w="16%">
                    {translate(
                      'pages:claims.bulkReconcile.columns.tariff',
                      'Tariff'
                    )}
                  </Table.Th>
                  {hasRejections && (
                    <Table.Th>
                      {translate(
                        'pages:claims.bulkReconcile.columns.outcome',
                        'Outcome'
                      )}
                    </Table.Th>
                  )}
                  {amountColumns.map(({ key, label }) => (
                    <Table.Th key={key} ta="right">
                      {label}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {previewRows.map((row) => (
                  <Table.Tr
                    key={
                      row.rowID ||
                      `${row.claimID}-${row.tariffLabel ?? TARIFF_FALLBACK}`
                    }
                  >
                    <Table.Td style={{ overflowWrap: 'anywhere' }}>
                      {row.claimLabel ?? claimNumberFallbackLabel}
                    </Table.Td>
                    <Table.Td>
                      <Text fw={600} size="xs">
                        {row.tariffLabel ?? tariffFallbackLabel}
                      </Text>
                    </Table.Td>
                    {hasRejections && (
                      <Table.Td>
                        {row.rejected
                          ? translate(
                              'pages:claims.bulkReconcile.unclaimable',
                              'Unclaimable'
                            )
                          : translate(
                              'pages:claims.bulkReconcile.paid',
                              'Paid'
                            )}
                      </Table.Td>
                    )}
                    {amountColumns.map(({ key, color }) => (
                      <Table.Td
                        key={key}
                        c={color}
                        ta="right"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {row.rejected && key === 'adjustmentAmount'
                          ? translate(
                              'pages:claims.bulkReconcile.noAdjustment',
                              'None'
                            )
                          : formatCurrency(row[key], locale)}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        </Paper>

        <Stack gap="xs">
          <Textarea
            label={translate(
              'pages:claims.bulkReconcile.providerCommentLabel',
              'Comment to Provider'
            )}
            placeholder={translate(
              'pages:claims.bulkReconcile.providerCommentPlaceholder',
              'Explain why these tariffs are being reconciled'
            )}
            value={comment}
            onChange={(event) => setComment(event.currentTarget.value)}
            disabled={loading}
            autosize
            minRows={3}
            error={comment.length > RECONCILIATION_COMMENT_MAX_LENGTH}
          />
          {eobs.map((eob) => (
            <Paper
              key={eob.id}
              withBorder
              radius="md"
              p="sm"
              bg="var(--mantine-color-default-hover)"
            >
              <Stack gap="xs">
                <Group gap="xs">
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                    {translate(
                      'pages:claims.bulkReconcile.payorExplanation',
                      'Payor’s explanation'
                    )}
                  </Text>
                  <Badge variant="default" radius="sm">
                    {translate(
                      'pages:claims.bulkReconcile.eobCode',
                      { code: eob.code },
                      `EOB ${eob.code}`
                    )}
                  </Badge>
                </Group>
                <Text size="sm">{eob.description}</Text>
                <Button
                  variant="default"
                  size="compact-xs"
                  radius="xl"
                  leftSection={<IconPlus size={13} />}
                  style={{ alignSelf: 'flex-start', borderStyle: 'dashed' }}
                  disabled={loading || !eob.description.trim()}
                  onClick={() =>
                    setComment((current) =>
                      [current.trimEnd(), eob.description]
                        .filter(Boolean)
                        .join('\n')
                    )
                  }
                >
                  {translate(
                    'pages:claims.bulkReconcile.addEobToComment',
                    'Add this to the comment'
                  )}
                </Button>
              </Stack>
            </Paper>
          ))}
          <Group justify="space-between" align="flex-start">
            <Text size="xs" c="dimmed">
              {translate(
                'pages:claims.bulkReconcile.providerCommentHelp',
                'Visible to the provider on each selected tariff.'
              )}
            </Text>
            <Text
              size="xs"
              c={
                comment.length > RECONCILIATION_COMMENT_MAX_LENGTH
                  ? 'red'
                  : 'dimmed'
              }
            >
              {translate(
                'pages:claims.bulkReconcile.charactersLeft',
                { count: RECONCILIATION_COMMENT_MAX_LENGTH - comment.length },
                `${
                  RECONCILIATION_COMMENT_MAX_LENGTH - comment.length
                } characters left`
              )}
            </Text>
          </Group>
        </Stack>
        <Stack gap="xs">
          <Textarea
            label={translate(
              'pages:claims.bulkReconcile.internalCommentLabel',
              'Internal Comment'
            )}
            placeholder={translate(
              'pages:claims.bulkReconcile.internalCommentPlaceholder',
              'Enter your internal comment here…'
            )}
            value={internalComment}
            onChange={(event) => setInternalComment(event.currentTarget.value)}
            disabled={loading}
            autosize
            minRows={3}
            error={internalComment.length > RECONCILIATION_COMMENT_MAX_LENGTH}
          />
          <Group justify="space-between" align="flex-start">
            <Text size="xs" c="dimmed">
              {translate(
                'pages:claims.bulkReconcile.internalCommentHelp',
                'Billing team only. Added to each selected claim’s internal comments.'
              )}
            </Text>
            <Text
              size="xs"
              c={
                internalComment.length > RECONCILIATION_COMMENT_MAX_LENGTH
                  ? 'red'
                  : 'dimmed'
              }
            >
              {translate(
                'pages:claims.bulkReconcile.charactersLeft',
                {
                  count:
                    RECONCILIATION_COMMENT_MAX_LENGTH - internalComment.length,
                },
                `${
                  RECONCILIATION_COMMENT_MAX_LENGTH - internalComment.length
                } characters left`
              )}
            </Text>
          </Group>
        </Stack>
        {commentsOverLimit && (
          <Alert color="red">
            {translate(
              'pages:claims.bulkReconcile.commentTooLong',
              'Each comment must be 500 characters or fewer.'
            )}
          </Alert>
        )}
        {saveError && (
          <Alert color="red">
            {translate(
              'pages:claims.bulkReconcile.saveError',
              'Reconciliation could not be completed. Your comments have been kept. Please try again.'
            )}
          </Alert>
        )}

        <Group grow align="stretch">
          {!onlyRejections && (
            <Button
              variant="outline"
              onClick={() => handleConfirm(true)}
              loading={loading}
              disabled={loading || commentsOverLimit}
              c={buttonTextColor(mode, loading)}
            >
              {translate(
                'pages:claims.bulkReconcile.reconcileWithAdjustment',
                'Reconcile with adjustment'
              )}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleConfirm(false)}
            loading={loading}
            disabled={loading || commentsOverLimit}
            c={buttonTextColor(mode, loading)}
          >
            {onlyRejections
              ? translate('pages:claims.bulkReconcile.reconcile', 'Reconcile')
              : translate(
                  'pages:claims.bulkReconcile.reconcileWithoutAdjustment',
                  'Reconcile without adjustment'
                )}
          </Button>
        </Group>

        {!onlyRejections && (
          <Text size="xs" c="dimmed" ta="center">
            {translate(
              'pages:claims.bulkReconcile.adjustmentExplanation',
              'Reconcile with adjustment: Record the variance and update the debit requested amount to match the paid amount. Reconcile without adjustment: Mark the tariff paid and leave debit amounts unchanged.'
            )}
          </Text>
        )}
      </Stack>
    </Modal>
  );
};
